import { useEffect, useState } from 'react';
import { grafanaExploreUrl } from '../lib/timeRange';
import type { ChartBoard } from '../lib/useChartBoard';
import { Chart } from './Chart';
import { TimeToolbar } from './TimeToolbar';

interface Props {
  board: ChartBoard;
  /** Which panels this grid draws, in order. */
  keys: string[];
  /**
   * Keep only the series whose legend label passes. The per-tenant view is
   * exactly this and nothing else: every response already carries one series
   * per tenant, so narrowing to one is a filter over data already in hand —
   * no refetch, no new endpoint, and no tenant parameter on `/charts/:panel`,
   * which stays the frozen allowlist `services/promClient.js:9-15` argues for.
   */
  filterSeries?: (label: string) => boolean;
  /** Drawn in place of the cards when the filter leaves nothing anywhere. */
  empty?: React.ReactNode;
}

/**
 * Whether the grid should give way to the "nothing for this filter" message.
 *
 * Only when a FILTER emptied panels that had data. With nothing loaded at all
 * — Prometheus unreachable, or not scraping sphere-watch, which is the usual
 * reason every tenant chart is blank — the cards must stay, because each one
 * says why; replacing them with "Nothing recorded for … in this window" hid the
 * reason behind a sentence that also named no tenant.
 */
export function showFilterEmpty(
  drawn: { loaded?: { series: unknown[] }; series: unknown[] }[],
  filtering: boolean,
): boolean {
  if (!filtering || !drawn.length) return false;
  const everyLoaded = drawn.every((one) => one.loaded);
  const filteredAway = drawn.some((one) => (one.loaded?.series.length ?? 0) > 0);
  return everyLoaded && filteredAway && drawn.every((one) => one.series.length === 0);
}

export function ChartGrid({ board, keys, filterSeries, empty }: Props) {
  const { meta, range, win, panels, refreshing, annotations } = board;
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpanded(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  if (!meta || !range || !win) return null;
  const byKey = new Map(meta.panels.map((panel) => [panel.key, panel]));

  const drawn = keys.map((key) => {
    const panel = byKey.get(key);
    const loaded = panels[key];
    const series = filterSeries
      ? (loaded?.series ?? []).filter((one) => filterSeries(one.label))
      : (loaded?.series ?? []);
    return { key, panel, loaded, series };
  });

  // A filter that matches nothing is not the same as a panel with no data, and
  // saying so beats eight identical "No data in this range." cards.
  if (showFilterEmpty(drawn, Boolean(filterSeries)) && empty) return <>{empty}</>;

  return (
    <div className="wt-chart-grid">
      {drawn.map(({ key, panel, loaded, series }) => {
        if (!panel) return null;
        // Drawn from sphere-watch's own history: Prometheus being unset or
        // unscraped says nothing about it, and Grafana would show it empty.
        const ours = panel.source === 'sphere-watch';
        return (
          <Chart
            key={key}
            title={panel.title}
            help={panel.help}
            unit={panel.unit}
            series={series}
            unavailable={loaded?.unavailable ?? (meta.available || ours ? null : 'Prometheus is not configured')}
            loading={!loaded}
            refreshing={refreshing}
            window={
              loaded?.from !== undefined && loaded?.to !== undefined
                ? { from: loaded.from, to: loaded.to }
                : win
            }
            step={loaded?.step ?? null}
            annotations={annotations}
            expanded={expanded === key}
            onToggleExpand={() => setExpanded((current) => (current === key ? null : key))}
            onZoom={board.onZoom}
            onZoomOut={board.onZoomOut}
            exploreUrl={
              meta.grafanaUrl && panel.query && !ours ? grafanaExploreUrl(meta.grafanaUrl, panel.query, range) : null
            }
            query={panel.query ?? null}
          />
        );
      })}
    </div>
  );
}

/**
 * Said ABOVE the charts when nothing is collecting sphere-watch's own /metrics.
 *
 * Every watch_* series — all of the Tenants charts, and the host, queue and
 * database panels on Vitals — reaches Prometheus only by Prometheus scraping
 * this process. When it does not, those cards are empty while the process_*
 * panels from the monitored apps keep drawing, and empty reads as "nobody did
 * anything". This process records every scrape it receives, so it can say so
 * with certainty instead of leaving the reader to guess.
 */
export function ScrapeNotice({ board }: { board: ChartBoard }) {
  const scrape = board.meta?.selfScrape;
  if (!board.meta?.available || !scrape || scrape.ok) return null;
  return (
    <div className="wt-banner wt-banner-warn">
      <strong>Nothing is collecting sphere-watch’s metrics</strong> — {scrape.problem}. The charts below are
      read back from Prometheus, so any drawn from sphere-watch’s own series stay empty until it scrapes
      this service. watch-api is behind Cloudflare, so the job must use <code>/scrape/metrics</code> with
      the <code>WATCH_METRICS_TOKEN</code> bearer token — see deploy/prometheus/scrape-config.yml.
    </div>
  );
}

/**
 * The time controls, wired to a board. Fifteen props, all of them the board's,
 * so a screen that has a board has a toolbar for one line.
 */
export function BoardToolbar({ board }: { board: ChartBoard }) {
  const { meta, range, nowSec, maxSpan, refreshKey, refreshing, updatedAt, showIncidents } = board;
  if (!meta || !range) return null;
  return (
    <TimeToolbar
      quickRanges={meta.quickRanges}
      range={range}
      nowSec={nowSec}
      maxSpanSeconds={maxSpan}
      onRange={board.changeRange}
      onShift={board.onShift}
      onZoomOut={board.onZoomOut}
      refreshKey={refreshKey}
      onRefreshKey={board.changeRefresh}
      onRefreshNow={board.refreshNow}
      refreshing={refreshing}
      updatedAt={updatedAt}
      showIncidents={showIncidents}
      onToggleIncidents={board.toggleIncidents}
      grafanaUrl={meta.grafanaUrl}
    />
  );
}
